import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Drawer, Space, Spin, Tag, Typography } from 'antd';
import api from '../api/client';

const { Text } = Typography;

const ROLE_COLORS: Record<string, string> = {
  supplier: 'blue',
  buyer: 'green',
  carrier: 'orange',
  bank: 'purple',
  government: 'red',
};

interface CpData {
  id: number;
  short_name: string;
  full_name?: string;
  inn?: string;
  kpp?: string;
  ogrn?: string;
  org_type?: string;
  legal_address?: string;
  actual_address?: string;
  phone?: string;
  email?: string;
  website?: string;
  notes?: string;
  is_active: boolean;
  roles: { role: string; label: string }[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  counterpartyId: number | null;
  width?: number;
  extra?: React.ReactNode;
  onContactClick?: (id: number) => void;
  refreshKey?: number;
}

const CounterpartyInfoDrawer: React.FC<Props> = ({
  open, onClose, counterpartyId, width = 460, extra, onContactClick, refreshKey,
}) => {
  const [data, setData] = useState<CpData | null>(null);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  useEffect(() => {
    if (!open || !counterpartyId) return;
    setLoading(true);
    setData(null);
    setContacts([]);
    api.get(`/counterparties/${counterpartyId}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));

    setContactsLoading(true);
    api.get('/individuals', {
      params: { role: 'counterparty_employee', counterparty_id: counterpartyId, per_page: 100 },
    })
      .then(r => setContacts(r.data.items || []))
      .catch(() => setContacts([]))
      .finally(() => setContactsLoading(false));
  }, [open, counterpartyId, refreshKey]);

  return (
    <Drawer
      title={data?.short_name || 'Контрагент'}
      open={open}
      onClose={onClose}
      width={width}
      extra={extra}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : data && (
        <>
          <Space wrap style={{ marginBottom: 16 }}>
            {data.is_active
              ? <Tag color="green">Активен</Tag>
              : <Tag color="default">Архив</Tag>}
            {data.roles.map(r => (
              <Tag key={r.role} color={ROLE_COLORS[r.role]}>{r.label}</Tag>
            ))}
          </Space>

          <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
            {data.full_name && (
              <Descriptions.Item label="Полное наименование">{data.full_name}</Descriptions.Item>
            )}
            {data.inn && <Descriptions.Item label="ИНН">{data.inn}</Descriptions.Item>}
            {data.kpp && <Descriptions.Item label="КПП">{data.kpp}</Descriptions.Item>}
            {data.ogrn && <Descriptions.Item label="ОГРН">{data.ogrn}</Descriptions.Item>}
            {data.org_type && (
              <Descriptions.Item label="Тип организации">{data.org_type}</Descriptions.Item>
            )}
            {data.legal_address && (
              <Descriptions.Item label="Юр. адрес">{data.legal_address}</Descriptions.Item>
            )}
            {data.actual_address && (
              <Descriptions.Item label="Факт. адрес">{data.actual_address}</Descriptions.Item>
            )}
            {data.phone && <Descriptions.Item label="Телефон">{data.phone}</Descriptions.Item>}
            {data.email && (
              <Descriptions.Item label="Email">
                <a href={`mailto:${data.email}`}>{data.email}</a>
              </Descriptions.Item>
            )}
            {data.website && (
              <Descriptions.Item label="Сайт">
                <a href={data.website} target="_blank" rel="noopener noreferrer">{data.website}</a>
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Контактные лица */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8 }}><Text strong>Контактные лица</Text></div>
            {contactsLoading ? (
              <Spin size="small" />
            ) : contacts.length === 0 ? (
              <div style={{ color: '#aaa', fontSize: 12 }}>Нет контактных лиц</div>
            ) : (
              contacts.map(c => {
                const name = [c.last_name, c.first_name, c.middle_name].filter(Boolean).join(' ');
                return (
                  <Card
                    key={c.id}
                    size="small"
                    style={{ marginBottom: 8, cursor: onContactClick ? 'pointer' : 'default' }}
                    onClick={onContactClick ? () => onContactClick(c.id) : undefined}
                  >
                    {name && <div><strong>{name}</strong></div>}
                    {c.job_title && <div style={{ color: '#555', fontSize: 13 }}>{c.job_title}</div>}
                    {c.phones?.map((p: string, i: number) => (
                      <div key={i} style={{ color: '#555', fontSize: 13 }}>{p}</div>
                    ))}
                    {c.emails?.map((e: string, i: number) => (
                      <div key={i} style={{ color: '#555', fontSize: 13 }}>
                        <a href={`mailto:${e}`} onClick={ev => ev.stopPropagation()}>{e}</a>
                      </div>
                    ))}
                    {c.notes && <div style={{ color: '#aaa', fontSize: 12 }}>{c.notes}</div>}
                  </Card>
                );
              })
            )}
          </div>

          {data.notes && (
            <div>
              <Text strong>Заметки</Text>
              <div style={{ marginTop: 8, color: '#555' }}>{data.notes}</div>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

export default CounterpartyInfoDrawer;
